/**
 * V2: Simple, deterministic company membership helpers
 * 
 * This module provides straightforward functions for:
 * - Creating a new company and admin user
 * - Loading user's company membership
 * 
 * No retries, no magic, just direct Firestore operations.
 */

import { db } from '../firebase/config';
import { doc, setDoc, getDoc, collection } from 'firebase/firestore';

/**
 * Generate a unique invite code (6 characters, uppercase alphanumeric)
 * For future use - not used in v1 but keeps the field in company doc
 */
function generateInviteCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing characters (0, O, I, 1)
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Create a new company and set the user as admin
 * 
 * This is the V2 simplified version - always creates a new company.
 * 
 * @param {string} userUid - Firebase Auth UID
 * @param {string} email - User email
 * @param {string} name - User display name
 * @param {Object|string} companyDataOrName - Company profile data object OR just company name (for backward compat)
 * @returns {Promise<string>} The created companyId
 */
export async function createCompanyV2(userUid, email, name, companyDataOrName) {
  console.log('[companyMembership] Creating company for user:', userUid);
  
  try {
    // Step 1: Create company document
    const companyRef = doc(collection(db, 'companies'));
    const companyId = companyRef.id;
    
    const timestamp = new Date().toISOString();
    
    // Handle both object and string (backward compatibility)
    const companyName = typeof companyDataOrName === 'string' 
      ? companyDataOrName 
      : companyDataOrName?.name || `${name}'s Company`;
    
    const companyProfileData = typeof companyDataOrName === 'object' 
      ? companyDataOrName 
      : {};
    
    const companyData = {
      name: companyName,
      ownerUid: userUid,
      createdAt: timestamp,
      inviteCode: generateInviteCode(), // For future use (v2+)
      plan: 'starter',
      // Include company profile fields if provided
      ...(companyProfileData.phone && { phone: companyProfileData.phone }),
      ...(companyProfileData.address && { address: companyProfileData.address }),
      ...(companyProfileData.email && { email: companyProfileData.email }),
      ...(companyProfileData.website && { website: companyProfileData.website }),
      ...(companyProfileData.licenseNumber && { licenseNumber: companyProfileData.licenseNumber }),
      settings: {
        defaultTaxRate: 0,
        defaultTerms: 'Net 30',
      },
    };
    
    await setDoc(companyRef, companyData);
    console.log('[companyMembership] ✅ Company created:', companyId);
    
    // Step 2: Create company user document (membership) FIRST
    // This must be created before the user doc so the onSnapshot listener can find it
    const companyUserRef = doc(db, 'companies', companyId, 'users', userUid);
    await setDoc(companyUserRef, {
      email: email,
      name: name,
      role: 'admin',
      isActive: true,
      createdAt: timestamp,
    });
    console.log('[companyMembership] ✅ Company user document created with role: admin');
    
    // Small delay to ensure Firestore has propagated the company user doc
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Step 3: Create top-level user document LAST
    // This triggers the onSnapshot listener, which now will find the company user doc above
    const userRef = doc(db, 'users', userUid);
    await setDoc(userRef, {
      email: email,
      name: name,
      companyId: companyId,
      createdAt: timestamp,
    });
    console.log('[companyMembership] ✅ User document created:', userUid);
    
    return companyId;
  } catch (error) {
    console.error('[companyMembership] ❌ Error creating company:', error);
    throw error;
  }
}

/**
 * Get user's company membership
 * 
 * Simple, deterministic: read /users/{uid} → companyId → /companies/{companyId}/users/{uid}
 * 
 * @param {string} userUid - Firebase Auth UID
 * @returns {Promise<{companyId: string, role: string, isActive: boolean, companyName?: string} | null>}
 */
export async function getUserCompanyMembershipV2(userUid) {
  console.log('[companyMembership] Loading membership for user:', userUid);
  
  try {
    // Step 1: Read top-level user document
    const userRef = doc(db, 'users', userUid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.warn('[companyMembership] ⚠️ User document not found:', userUid);
      return null;
    }
    
    const userData = userDoc.data();
    const companyId = userData.companyId;
    
    if (!companyId) {
      console.warn('[companyMembership] ⚠️ User has no companyId:', userUid);
      return null;
    }
    
    console.log('[companyMembership] Found companyId:', companyId);
    
    // Step 2: Read company user document (membership)
    const companyUserRef = doc(db, 'companies', companyId, 'users', userUid);
    const companyUserDoc = await getDoc(companyUserRef);
    
    if (!companyUserDoc.exists()) {
      console.warn('[companyMembership] ⚠️ Company user document not found:', companyId, userUid);
      return null;
    }
    
    const companyUserData = companyUserDoc.data();
    console.log('[companyMembership] ✅ Loaded membership - role:', companyUserData.role);
    
    // Step 3: Optionally read company document for name
    let companyName = null;
    try {
      const companyRef = doc(db, 'companies', companyId);
      const companyDoc = await getDoc(companyRef);
      if (companyDoc.exists()) {
        companyName = companyDoc.data().name;
      }
    } catch (err) {
      console.warn('[companyMembership] Could not load company name:', err);
      // Non-critical, continue without company name
    }
    
    return {
      companyId: companyId,
      role: companyUserData.role || 'tech',
      isActive: companyUserData.isActive !== false,
      companyName: companyName || undefined,
      userData: companyUserData,
    };
  } catch (error) {
    console.error('[companyMembership] ❌ Error loading membership:', error);
    return null;
  }
}

